'use client';

// The composer's microphone (idle state). While dictation runs the composer
// shows its recording row instead (./dictation.tsx). There is no language
// control: engines detect the spoken language. When the microphone cannot
// start it stays visible but disabled, with the localized reason as its
// description, instead of recording and then failing.
import { Button } from '@/components/ui/button';
import { useNamespaceCopy } from '@/i18n/ns';
import speechCopy from '@/i18n/ns/speech';
import { cn } from '@/lib/utils';
import { MicIcon } from 'lucide-react';
import { useId, type FC } from 'react';
import { useSpeech, useSpeechState } from './speech-provider';

export const SpeechControl: FC = () => {
  const speech = useSpeech();
  const copy = useNamespaceCopy(speechCopy);
  const phase = useSpeechState((state) => state.phase);
  const reasonId = useId();
  if (!speech) return null;
  const reason = speech.blocked ?? (phase === 'pending-insert' ? 'pendingInsert' : null);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'aui-composer-dictate size-11 rounded-full md:size-7',
          reason && 'text-muted-foreground/60 cursor-not-allowed hover:bg-transparent',
        )}
        aria-label={copy.start}
        aria-disabled={reason ? true : undefined}
        aria-describedby={reason ? reasonId : undefined}
        title={reason ? copy[reason] : copy.start}
        data-speech-blocked={reason ?? undefined}
        onClick={() => {
          if (!reason) void speech.controller.start();
        }}
      >
        <MicIcon className="aui-composer-dictate-icon size-5 md:size-4" />
      </Button>
      {reason && (
        <span id={reasonId} className="sr-only">
          {copy[reason]}
        </span>
      )}
    </>
  );
};
